import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';
import { NextSeo } from 'next-seo';
import { ChangeEvent, useMemo, useState } from 'react';
import { findResultsState } from 'react-instantsearch-dom/server';

import { Box, Col, Container, Flex, Row } from '@madeiramadeirabr/layout-web';
import { Text } from '@madeiramadeirabr/text-web';

import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { TextField } from '@madeiramadeirabr/text-field-web';

import { LISTING_INDEXES } from '@helpers/algolia/constants';
import getSellerBySlug from '@helpers/api/seller/get-seller-by-slug';
import { splitUrlWithPage } from '@helpers/common/router';
import { SELLER_PAGE_CACHE_TIME } from '@helpers/constants/cache';
import { FEATURE_FLAGS } from '@helpers/constants/feature-flags';
import { buildPaginatedSeo } from '@helpers/seo';
import { fetchIndexFacets, getAlgoliaClientLite } from '@services/algolia';
import { getRegionalSearchConfig, TRegionalSearchConfig } from '@services/bff-content-hub/datocms';
import { getGraphqlCommonData } from '@services/datocms/common';
import { getFeatureFlagActive } from '@services/datocms/feature-flag';
import { formatFacetsConfig } from '@services/datocms/helpers/taxonomy';
import {
  formatFiltersFixedMobile,
  getServerlessFixedAndDefaultFiltersData,
} from '@services/datocms/search/getFixedAndDefaultFilters';

import ListingPage from '@app/common/cavilha-v2/ListingPage';
import TextCollapse from '@app/common/TextCollapse';
import Layout, { LayoutProps } from '@app/layout/Layout';
import { WishlistProvider } from 'contexts/wishlist';
import { BANNER_TYPE } from 'types/highlight-banner/highlightBanner.types';

export async function getStaticPaths(): Promise<GetStaticPathsResult<any>> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps({
  preview = false,
  params,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<any>> {
  const { slug: paramSlug = '' } = params;

  const slug = Array.isArray(paramSlug) ? `${paramSlug.join('/')}` : paramSlug;

  const { url: sellerSlug, page } = splitUrlWithPage(slug);
  const commonSiteData: any = await getGraphqlCommonData({
    preview,
    bannerPage: BANNER_TYPE.SELLERS,
  });

  const searchClient = getAlgoliaClientLite();
  const seller = await getSellerBySlug({ slug: sellerSlug });

  const redirectLandingLojista = {
    redirect: {
      destination: '/lojista',
      statusCode: 301,
    },
  } as GetStaticPropsResult<any>;

  const [allFacets, flagActiveRegionalSearch, regionalSearchConfig]: [
    any,
    boolean,
    TRegionalSearchConfig,
  ] = await Promise.all([
    fetchIndexFacets(LISTING_INDEXES[0].value),
    getFeatureFlagActive(FEATURE_FLAGS.REGIONAL_SEARCH),
    getRegionalSearchConfig(),
  ]);
  const ruleContexts = ['productListingAtlas', 'listingPageAtlas', 'toggles'];

  if (!seller) {
    return redirectLandingLojista;
  }

  const httpDomain = process.env.NEXT_PUBLIC_HTTP_DOMAIN_PRINCIPAL
    ? process.env.NEXT_PUBLIC_HTTP_DOMAIN_PRINCIPAL
    : process.env.NEXT_PUBLIC_HTTP_DOMAIN;

  const { nome } = seller;
  const pageParam = page > 1 ? `/${page}` : '';
  const canonical = `${httpDomain}/lojista/${seller.slug}${pageParam}`;

  const fixedAndDefaultFilter = await getServerlessFixedAndDefaultFiltersData({ preview });

  const newFormattedFixedFilters = formatFiltersFixedMobile(
    fixedAndDefaultFilter?.fixedFilter || {}
  );

  const parsedFilters = formatFacetsConfig([], fixedAndDefaultFilter);

  const resultsState = await findResultsState(ListingPage, {
    searchClient,
    indexName: LISTING_INDEXES[0].value,
    allFacets,
    initialFilters: [`sellers_slug:${sellerSlug}`],
    ruleContexts,
    cmsConfig: { productcardVariations: false },
    sellerSlug,
    pageQuery: '',
  });

  if (resultsState.rawResults[0].hits.length === 0) {
    return redirectLandingLojista;
  }

  return {
    props: {
      ...commonSiteData,
      preview,
      allFacets,
      canonical,
      ruleContexts,
      resultsState: JSON.parse(JSON.stringify(resultsState)),
      seller,
      seo: buildPaginatedSeo({
        seo: {
          title: `${nome} em Promoção | Compre na MadeiraMadeira`,
          description: `Compre produtos de ${nome} na MadeiraMadeira. Aproveite as promoções e preços baixos de ${nome}!`,
        },
        page,
        name: nome,
      }),
      defaultFilters: {
        facetsConfig: parsedFilters,
        visualFilters: {},
      },
      filtersFixedMobile: newFormattedFixedFilters,
      flagActiveRegionalSearch,
      regionalSearchConfig,
    },
    revalidate: SELLER_PAGE_CACHE_TIME,
  };
}

type SellerPageProps = LayoutProps & {
  allFacets: any;
  ruleContexts: string[];
  resultsState: any;
  seo?: Record<string, any>;
  canonical: string;
  seller?: any;
  defaultFilters?: any;
  filtersFixedMobile?: Record<string, any>[];
  flagActiveRegionalSearch?: boolean;
};

export default function Seller({
  allFacets,
  ruleContexts,
  resultsState,
  seo,
  canonical,
  seller,
  defaultFilters,
  filtersFixedMobile,
  flagActiveRegionalSearch = false,
}: SellerPageProps): JSX.Element {
  // Client estável: o router.replace pós-hidratação (ISR) re-renderiza a página e um client novo
  // faria o InstantSearch buscar de novo (novo queryID → ListView duplicado).
  const searchClient = useMemo(() => getAlgoliaClientLite(), []);
  const { logo, descricao, slug, nome } = seller;
  const [inputSearch, setInputSearch] = useState('');

  return (
    <>
      <NextSeo
        canonical={canonical}
        title={seo?.title ?? ''}
        description={seo?.description ?? ''}
        twitter={{
          cardType: 'summary',
        }}
        openGraph={{
          type: 'website',
          title: seo?.title ?? '',
          description: seo?.description ?? '',
        }}
      />
      <Container css={{ px: '$none' }}>
        <Row
          css={{
            $mdDown: { flexDirection: 'column-reverse' },
          }}
        >
          <Col sm={12} md={8}>
            <Box css={{ mt: '$xlarge' }}>
              <Box css={{ mb: '$small', mt: '$medium' }}>
                <Text
                  as="h1"
                  color="$text-default"
                  size="h3"
                  css={{
                    whiteSpace: 'break-spaces',
                  }}
                >
                  {nome}
                </Text>
              </Box>
              {descricao ? (
                <TextCollapse>
                  <Text
                    as="p"
                    color="$text-default"
                    size="h7"
                    weight="regular"
                    css={{
                      letterSpacing: '$small',
                      lineHeight: '$small',
                    }}
                  >
                    {descricao}
                  </Text>
                </TextCollapse>
              ) : null}
              <Box
                css={{
                  mt: '$large',
                  mb: '$small',
                  width: '288px',
                  '& > div': {
                    width: '100%',
                  },
                  $smDown: { width: '100%' },
                }}
              >
                <TextField
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setInputSearch(e.target.value)}
                  value={inputSearch}
                  placeholder="Buscar Produtos"
                  as="input"
                  icon={faSearch}
                />
              </Box>
            </Box>
          </Col>

          <Col sm={12} md={4}>
            <Flex
              justify="start"
              css={{
                mt: '$medium',
                $md: { justifyContent: 'flex-end', mt: '$xlarge' },
              }}
            >
              {logo ? (
                <Flex
                  justify="center"
                  align="center"
                  css={{
                    width: '150px',
                    height: '150px',
                    border: '1px solid',
                    borderColor: '$border-divider',
                    borderRadius: '$small',
                    '& img': {
                      maxHeight: '100%',
                      maxWidth: '100%',
                    },
                    $mdDown: { width: '88px', height: '88px' },
                  }}
                >
                  <img src={`${logo}`} alt={nome} loading="lazy" />
                </Flex>
              ) : null}
            </Flex>
          </Col>
        </Row>
      </Container>
      <WishlistProvider>
        <ListingPage
          searchClient={searchClient}
          indexName={LISTING_INDEXES[0].value}
          allFacets={allFacets}
          initialFilters={[`sellers_slug:${slug}`]}
          ruleContexts={ruleContexts}
          resultsState={resultsState}
          cmsConfig={{
            productcardVariations: false,
            productcardSetSeller: true,
          }}
          query={inputSearch}
          sellerSlug={slug}
          taxonomy={defaultFilters}
          filtersFixedMobile={filtersFixedMobile}
          regionalSearch={flagActiveRegionalSearch}
        />
      </WishlistProvider>
    </>
  );
}

Seller.Layout = Layout;
